import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { API_URL, currentMonthPL, fmtH, fmtPLN, godzinyApi } from './shared';

const WorkerRow = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--crm-border)]/50" data-testid={`summary-row-${r.worker_id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-4 py-3 text-left">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span className="font-semibold text-[var(--crm-text-primary)] min-w-[160px]">{r.name}</span>
        <span className="font-mono" data-testid={`summary-hours-${r.worker_id}`}>{fmtH(r.hours)}</span>
        <span className="text-sm text-[var(--crm-text-secondary)]" data-testid={`summary-rates-${r.worker_id}`}>
          {r.rates.map((x) => `${x.stawka} zł/h × ${fmtH(x.hours)}`).join(' + ')}
        </span>
        <span className="ml-auto font-bold text-emerald-500" data-testid={`summary-amount-${r.worker_id}`}>{fmtPLN(r.amount)}</span>
      </button>
      {open && (
        <div className="overflow-x-auto pb-3">
          <table className="w-full text-sm">
            <thead><tr className="text-[var(--crm-text-muted)] text-left">
              <th className="p-1">Data</th><th className="p-1">Od–do</th><th className="p-1">Godz.</th><th className="p-1">Miejsce pracy</th>
              <th className="p-1 text-right">Stawka</th><th className="p-1 text-right">Kwota</th></tr></thead>
            <tbody>{r.days.map((d, i) => (
              <tr key={i} className="text-[var(--crm-text-secondary)]">
                <td className="p-1 font-mono">{d.date}</td><td className="p-1 font-mono">{d.start}–{d.end}</td><td className="p-1">{fmtH(d.hours)}</td>
                <td className="p-1">{d.place}</td><td className="p-1 text-right">{d.stawka} zł/h</td><td className="p-1 text-right">{fmtPLN(d.amount)}</td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const SummaryTab = () => {
  const [month, setMonth] = useState(currentMonthPL());
  const [data, setData] = useState(null);
  const load = useCallback(() => godzinyApi.summary(month).then(setData).catch(() => toast.error('Błąd zestawienia')), [month]);
  useEffect(() => { load(); }, [load]);
  const exportXlsx = async () => {
    const res = await axios.get(`${API_URL}/godziny/summary/export`, { params: { month }, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `godziny_${month}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4" data-testid="summary-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="text-xs text-[var(--crm-text-secondary)] block mb-1">Miesiąc</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="summary-month-input" /></div>
        <Button onClick={exportXlsx} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="summary-export-btn">
          <Download className="w-4 h-4 mr-1" />Eksport do Excela</Button>
      </div>
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        {!data?.rows.length ? <p className="text-[var(--crm-text-muted)]">Brak zatwierdzonych godzin w tym miesiącu.</p>
          : data.rows.map((r) => <WorkerRow key={r.worker_id} r={r} />)}
        {data?.rows.length > 0 && (
          <div className="flex justify-end gap-6 pt-3 font-semibold text-[var(--crm-text-primary)]" data-testid="summary-total">
            <span>Razem: {fmtH(data.total_hours)}</span><span className="text-emerald-500">{fmtPLN(data.total_amount)}</span>
          </div>
        )}
      </Card>
    </div>
  );
};
